import {
	type BatchPageCapExceededRes,
	DsaiBatchPageCapExceededCode,
	type IngestionFilePagesRes,
} from '@dealstack/shared/lib/generated';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date';
import { isRecord } from '../dealstackAi.utils';
import { INGEST_PANEL } from './ingestion.constants';
import type { TranslationRef } from '../AnalysisWorkspace/components/primitives/primitives.types';
import type { FilePagesView, PageCapSplit } from './ingestion.types';

const INGEST_PANELS: string[] = Object.values(INGEST_PANEL);

const isIngestPanel = (value: string): value is INGEST_PANEL => INGEST_PANELS.includes(value);

export const parseIngestPanel = (value: string | null): INGEST_PANEL | null =>
	value !== null && isIngestPanel(value) ? value : null;

/** "Apr 15, 2024": the same calendar day in every time zone. */
export const formatLibraryDate = (isoDate: string): string =>
	formatBEDateToUiWithFallback(isoDate, DATE_FORMATS.MONTH_DAY_YEAR_UNPADDED);

/** A whole count with thousands separators; zero stays "0". */
export const formatCount = (value: number): string =>
	formatNumber({ value, minPrecision: 0, returnNumericalZero: true });

/** A rate to one decimal below 10, whole above: "6.8", "17". */
export const formatRate = (value: number): string =>
	value < 10
		? formatNumber({
				value: Math.round(value * 10) / 10,
				minPrecision: 1,
				lockPrecision: true,
				returnNumericalZero: true,
			})
		: formatCount(Math.round(value));

/** The page count and the reasons a file will not be read in full. */
export const filePagesView = (pages: IngestionFilePagesRes | null): FilePagesView => {
	if (pages === null) {
		return { pages: null, warnings: [] };
	}
	const warnings: TranslationRef[] = [];
	if (!pages.readable) {
		warnings.push({ key: 'dealstack_ai.ingest.file.unreadable' });
	}
	if (pages.passwordProtected) {
		warnings.push({ key: 'dealstack_ai.ingest.file.password_protected' });
	}
	if (pages.tooManyPages) {
		warnings.push({ key: 'dealstack_ai.ingest.file.too_many_pages' });
	}
	return {
		pages: { key: 'dealstack_ai.ingest.file.pages', values: { count: pages.pages } },
		warnings,
	};
};

/** Pages a file counts towards the batch cap: none when it is over the per-document cap. */
export const pagesTowardsCap = (pages: IngestionFilePagesRes | null): number =>
	pages === null || pages.tooManyPages ? 0 : pages.pages;

/** The 422 a start over the page cap returns without the confirmed split. */
export const isBatchPageCapExceeded = (error: unknown): error is BatchPageCapExceededRes =>
	isRecord(error) &&
	error.code === DsaiBatchPageCapExceededCode.BATCH_PAGE_CAP_EXCEEDED &&
	typeof error.capPages === 'number' &&
	typeof error.pages === 'number';

/** The split the server accepts: the page total over the cap, rounded up. */
export const toPageCapSplit = ({ pages, capPages }: BatchPageCapExceededRes): PageCapSplit => ({
	pages,
	capPages,
	batchCount: Math.max(1, Math.ceil(pages / capPages)),
});
